import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import {
  useCreatePaymentMethod,
  useFetchEntidadesFinancieras,
  useUpdatePaymentMethod,
} from '@/shared/actions/erp';
import { CustomAutocomplete, CustomTextField, SampleCheckbox, SingleFormBoxScene } from '@/shared/components/ui';
import { IPaymentMethod } from '@/shared/interfaces/erp/system-settings';
import { newPaymentMethodSchema } from '@/shared/utils';
import { gridSizeMdLg3, TipoContratoEnumChoice, TipoFormaPagoEnumChoice } from '@/shared';
import { EntidadesFinancieras, Tarjeta } from '@/shared/interfaces';
import { useFetchTarjetas } from '@/shared/actions/erp/administration/tarjeta.actions';


export type SavePaymentMethodProps = {
  editPaymentMethod?: IPaymentMethod;
  titlePage: string;
};

type SaveFormData = {
  //codigo: string;
  nombre: string;
  tipo_contrato: string;
  forma_pago: string;
  estado: boolean;
  aplica_home?: boolean
  aplica_pyme?: boolean
  aplica_corporativo?: boolean
  entidad_financiera?: string;
  tarjeta?: string;
  alias?: string;
  id_formapago?: string
  fecha_creacion?: string
  fecha_modificacion?: string

};

const returnUrlPayment = '/parametrizacion/metodo-pago';

const SavePaymentMethod: React.FC<SavePaymentMethodProps> = ({
  titlePage,
  editPaymentMethod,
}) => {
  const navigate = useNavigate();

  const [shrink, setShrink] = useState(false);
  const [showTarjetas, setShowTarjetas] = useState(false);


  ///* mutations
  const createPaymentMethodMutation = useCreatePaymentMethod({
    navigate,
    returnUrl: returnUrlPayment
  });
  const updatePaymentMethodMutation = useUpdatePaymentMethod({
    navigate,
    returnUrl: returnUrlPayment
  }
  );
  const metodosPagoOptions = Object.entries(TipoContratoEnumChoice).map(
    ([key, value]) => ({ label: key, value })
  );
  const formaPagoOptions = Object.entries(TipoFormaPagoEnumChoice).map(
    ([key, value]) => ({ label: key, value })
  );
  const onChangeEntidadFinanciera = (value: string) => {
    form.setValue('entidad_financiera', value);
  }
  const onChangeTarjetas = (value: string) => {
    form.setValue('tarjeta', value);
  }
  ///* form
  const form = useForm<SaveFormData>({
    resolver: yupResolver(newPaymentMethodSchema),
    defaultValues: { estado: true },
  });
  const {
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = form;

  ///* effects
  useEffect(() => {
    if (!editPaymentMethod?.id_formapago) return;
    setShrink(true);
    reset({ ...editPaymentMethod } as any);
  }, [editPaymentMethod]);

  ///* handlers
  const onSave = async () => {
    if (!isValid) return;

    ///* edit
    ///* edit
    if (editPaymentMethod?.id_formapago) {
      return updatePaymentMethodMutation.mutate({
        id: editPaymentMethod.id_formapago,
        data: form.getValues() as any,
      });
    }


    ///* create
    return createPaymentMethodMutation.mutate(form.getValues() as any,);
  };
  const { data: EntidadesFinancierasPaging, isLoading: isLoadingEntidadesFinancieras } = useFetchEntidadesFinancieras({
    page_size: 600,
  });
  const { data: TarjetasPaging, isLoading: isLoadingTarjetas } = useFetchTarjetas({
    page_size: 600,
  });


  return (
    <SingleFormBoxScene
      titlePage={titlePage}
      onCancel={() => {
        navigate(returnUrlPayment);
      }}
      onSave={handleSubmit(onSave)}
    >

      <CustomTextField
        label="Nombre"
        name='nombre'
        control={form.control}
        error={errors.nombre}
        helperText={errors.nombre?.message}
        shrink={shrink}
      />
      <CustomTextField
        label="Alias"
        name='alias'
        control={form.control}
        error={errors.alias}
        helperText={errors.alias?.message}
        shrink={shrink}
      />
      <CustomAutocomplete
        label="Tipo de contrato"
        name="tipo_contrato"
        control={form.control}
        defaultValue={form.getValues().tipo_contrato || ''}
        error={errors.tipo_contrato}
        helperText={errors.tipo_contrato?.message}
        options={metodosPagoOptions}
        isLoadingData={false}
        valueKey="value"
        optionLabelForEdit={form.getValues().tipo_contrato}
      />

      <CustomAutocomplete
        label="Forma de Pago"
        name="forma_pago"
        control={form.control}
        defaultValue={form.getValues().forma_pago || ''}
        error={errors.forma_pago}
        helperText={errors.forma_pago?.message}
        options={formaPagoOptions}
        isLoadingData={false}
        valueKey="value"
        optionLabelForEdit={form.getValues().forma_pago}
      />

      <CustomAutocomplete<EntidadesFinancieras>
        label="Entidades financieras"
        name="entidades_fianncieras"
        //// options
        options={EntidadesFinancierasPaging?.data.data || []}
        // label & search by name
        valueKey="nombre"
        actualValueKey='codigo'
        //
        defaultValue={form.getValues().entidad_financiera || ''}
        optionLabelForEdit={form.getValues().entidad_financiera || ''}
        isLoadingData={isLoadingEntidadesFinancieras}
        //// validation
        control={form.control}
        error={errors.entidad_financiera}
        helperText={errors.entidad_financiera?.message}
        onChangeValue={onChangeEntidadFinanciera}
      />


      <SampleCheckbox
        label="Aplicar Corporativo"
        name="aplica_corporativo"
        control={form.control}
        defaultValue={false}
        size={gridSizeMdLg3}
      />

      <SampleCheckbox
        label="Aplicar PYME"
        name="aplica_pyme"
        control={form.control}
        defaultValue={false}
        size={gridSizeMdLg3}
      />

      <SampleCheckbox
        label="Aplicar Hogar"
        name="aplica_home"
        control={form.control}
        defaultValue={false}
        size={gridSizeMdLg3}
      />

      <SampleCheckbox
        label="¿Crear metodo de pago con tarjetas?"
        name="llevaTarjetas"
        control={form.control}
        defaultValue={false}
        onChangeValue={(e) => {
          setShowTarjetas(e!);
        }}
      />
      {showTarjetas && (
        <CustomAutocomplete<Tarjeta>
          label="Tarjetas"
          name="tarjetas"
          options={TarjetasPaging?.data.data || []}
          valueKey="nombre"
          actualValueKey='codigo'
          defaultValue={form.getValues().tarjeta || ''}
          optionLabelForEdit={form.getValues().tarjeta || ''}
          isLoadingData={isLoadingTarjetas}
          control={form.control}
          error={errors.tarjeta}
          helperText={errors.tarjeta?.message}
          onChangeValue={onChangeTarjetas}
        />
      )}
      <SampleCheckbox
        label="Estado"
        name="estado"
        control={form.control}
        defaultValue={!!form.getValues().estado}
        isState
      />
    </SingleFormBoxScene>
  );
};

export default SavePaymentMethod;